/**
 * External Dependencies
 */
import Cookies from 'js-cookie';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Route,
  Routes as ReactRouterRoutes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

/**
 * Internal Dependencies
 */
import { updateAuth as actionUpdateAuth } from './actions';
import ErrorBoundary from './ErrorBoundary';
import RoutesList from './pages';
import NotFoundPage from './pages/404';

const EXCLUDED_PATHS = ['/403', '/sign-in', '/404'];
const EXCLUDED_PATH_PREFIXES = [
  'success',
  'error',
  'monitor-l',
  'totem-l',
  'display-l',
  'ambulatoriale-light',
  'ambulatoriale-code',
  'ambulatoriale-code-full',
  'reset-password',
];
const POLICY_EXCLUDED_PREFIXES = [...EXCLUDED_PATH_PREFIXES, 'informativa'];

/**
 * Component
 */
const Routes = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = useRef(location.pathname);

  const checkPermission = useCallback(() => {
    window.api.risorse
      .pathAuth(window.location.pathname)
      .then(res => {
        setLoading(false);
        if (!res.data.autorizzato) {
          navigate('/403');
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, [navigate]);

  const checkLoggedIn = useCallback(() => {
    window.swal.close();
    setLoading(true);

    const pathPrefix = window.location.pathname.split('/')[1];

    if (
      EXCLUDED_PATHS.includes(location.pathname) ||
      EXCLUDED_PATH_PREFIXES.includes(pathPrefix)
    ) {
      setLoading(false);
    } else {
      window.api.auth
        .loggedIn()
        .then(res => {
          if (!res.data.loggedIn) {
            dispatch(actionUpdateAuth({ token: '' }));
          } else {
            dispatch(actionUpdateAuth({ token: 'true' }));
            checkPermission();
          }
        })
        .catch(() => {
          dispatch(actionUpdateAuth({ token: '' }));
        });
    }
  }, [location.pathname, dispatch, checkPermission]);

  const checkPolicy = useCallback(() => {
    const pathPrefix = window.location.pathname.split('/')[1];

    if (
      EXCLUDED_PATHS.includes(location.pathname) ||
      POLICY_EXCLUDED_PREFIXES.includes(pathPrefix)
    ) {
      return;
    }

    if (Cookies.get('policy') !== 'accepted') {
      window.api.auth
        .getPolicy()
        .then(res => {
          if (!res.data.policy) {
            navigate('/informativa');
          } else {
            const inFourHours = new Date(
              new Date().getTime() + 240 * 60 * 1000
            );
            Cookies.set('policy', 'accepted', {
              expires: inFourHours,
            });
          }
        })
        .catch(() => {
          dispatch(actionUpdateAuth({ token: '' }));
        });
    }
  }, [location.pathname, dispatch, navigate]);

  // Initial mount
  useEffect(() => {
    checkLoggedIn();
    checkPolicy();
  }, []);

  // On pathname change
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      checkLoggedIn();
      checkPolicy();
    }
  }, [location.pathname, checkLoggedIn, checkPolicy]);

  return (
    <ErrorBoundary>
      <ReactRouterRoutes location={location}>
        {Object.keys(RoutesList).map(path => {
          const RouteInner = RoutesList[path];

          return (
            <Route
              key={path}
              path={path}
              element={!loading ? <RouteInner /> : <></>}
            />
          );
        })}

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </ReactRouterRoutes>
    </ErrorBoundary>
  );
};

export default Routes;
